import { useCallback, useEffect, useRef, useState } from 'react';

const PERMISSION_DENIED_MESSAGE =
  'Не удалось получить доступ к камере. Разрешите доступ в настройках браузера или загрузите фото из галереи.';

interface UseCameraStreamOptions {
  facingMode: 'environment' | 'user';
  enabled?: boolean;
}

interface UseCameraStreamResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  error: string | null;
  isReady: boolean;
  captureFrame: () => Promise<Blob | null>;
}

export function useCameraStream({
  facingMode,
  enabled = true,
}: UseCameraStreamOptions): UseCameraStreamResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setIsReady(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      stopStream();
      return;
    }

    let cancelled = false;

    async function startCamera() {
      setError(null);
      setIsReady(false);

      if (!navigator.mediaDevices?.getUserMedia) {
        setError(PERMISSION_DENIED_MESSAGE);
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode } },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;

        if (video) {
          video.srcObject = stream;
          await video.play();
          setIsReady(true);
        }
      } catch {
        if (!cancelled) {
          setError(PERMISSION_DENIED_MESSAGE);
        }
      }
    }

    void startCamera();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [enabled, facingMode, stopStream]);

  const captureFrame = useCallback(async (): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    if (facingMode === 'user') {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    });
  }, [facingMode]);

  return { videoRef, error, isReady, captureFrame };
}
